import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import baseURL from './url';
import storage from './storage';
import { pedirBootstrap } from './DataContext';

const DEFAULT_COLOR = '#0D9488';
const COLOR_NAVEGADOR = '#0f172a';

const ThemeContext = createContext({ color: DEFAULT_COLOR, cargarColor: () => {} });

function clamp(n, min, max) {
    return Math.min(max, Math.max(min, n));
}

function hexToRgb(hex) {
    const h = hex.replace('#', '');
    return {
        r: parseInt(h.substring(0, 2), 16),
        g: parseInt(h.substring(2, 4), 16),
        b: parseInt(h.substring(4, 6), 16),
    };
}

function rgbToHex(r, g, b) {
    const p = (n) => clamp(Math.round(n), 0, 255).toString(16).padStart(2, '0');
    return `#${p(r)}${p(g)}${p(b)}`.toUpperCase();
}

export function aclarar(hex, factor) {
    const { r, g, b } = hexToRgb(hex);
    return rgbToHex(r + (255 - r) * factor, g + (255 - g) * factor, b + (255 - b) * factor);
}

export function oscurecer(hex, factor) {
    const { r, g, b } = hexToRgb(hex);
    return rgbToHex(r * (1 - factor), g * (1 - factor), b * (1 - factor));
}

export function derivarTema(hex) {
    return {
        '--color1': hex,
        '--color1-dark': oscurecer(hex, 0.15),
        '--color1-soft': `rgba(${hexToRgb(hex).r}, ${hexToRgb(hex).g}, ${hexToRgb(hex).b}, 0.08)`,
        '--color3': `rgba(${hexToRgb(hex).r}, ${hexToRgb(hex).g}, ${hexToRgb(hex).b}, 0.25)`,
        '--loading': `${hex}79`,
        '--shadow-hover': `0 10px 28px rgba(${hexToRgb(hex).r}, ${hexToRgb(hex).g}, ${hexToRgb(hex).b}, 0.22)`,
        '--gradient1': `linear-gradient(135deg, #312e81, ${hex})`,
    };
}

function aplicarTema(color, colorNavegador) {
    const vars = derivarTema(color);
    Object.keys(vars).forEach((k) => {
        document.documentElement.style.setProperty(k, vars[k]);
    });
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
        meta.setAttribute('content', (colorNavegador || color).toUpperCase());
    }
}

export function ThemeProvider({ children }) {
    const [color, setColor] = useState(() => storage.getItem('color') || '');

    useEffect(() => {
        const guardado = storage.getItem('color');
        if (guardado && /^#[0-9A-Fa-f]{6}$/.test(guardado)) {
            aplicarTema(guardado.toUpperCase());
        }
        const aplicarColor = (nuevo) => {
            if (nuevo && /^#[0-9A-Fa-f]{6}$/.test(nuevo)) {
                storage.setItem('color', nuevo.toUpperCase());
                setColor(nuevo.toUpperCase());
                aplicarTema(nuevo.toUpperCase());
            } else {
                storage.removeItem('color');
                setColor('');
                aplicarTema(DEFAULT_COLOR, COLOR_NAVEGADOR);
            }
        };
        pedirBootstrap()
            .then(data => aplicarColor(data?.color))
            .catch(() => {
                fetch(`${baseURL}/temaGet.php`)
                    .then(r => r.json())
                    .then(data => aplicarColor(data?.color))
                    .catch(() => {});
            });
    }, []);

    const cargarColor = useCallback((nuevo) => {
        if (!nuevo || !/^#[0-9A-Fa-f]{6}$/.test(nuevo)) return;
        storage.setItem('color', nuevo.toUpperCase());
        setColor(nuevo.toUpperCase());
        aplicarTema(nuevo.toUpperCase());
    }, []);

    return (
        <ThemeContext.Provider value={{ color: color || DEFAULT_COLOR, cargarColor }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    return useContext(ThemeContext);
}

export function colorTema() {
    if (typeof document === 'undefined') return DEFAULT_COLOR;
    const v = getComputedStyle(document.documentElement).getPropertyValue('--color1').trim();
    return /^#[0-9A-Fa-f]{6}$/.test(v) ? v : DEFAULT_COLOR;
}
